import React, { useState, useMemo } from 'react'
import { Chip, Box, Tooltip } from '@mui/material'
import GenericButton from '../../../components/GenericButton'
import { ButtonType } from '../../../components/GenericButton/styles'
import { SlotStatus } from '../../../models/DistributionPlan'
import { useTheme } from '@mui/material/styles'
import { getSlotSectionApi, updateSlotStatusApi, type ISlotStatusWithMessage } from '../../../store/slices/distributionPlanSlice/apis'
import { useAppDispatch } from '../../../store'
import PageHeader from '../../../components/PageHeader'
import { type CustomBreadcrumbT } from '../../../utils/BreadcrumbDataPreparation'
import GenericModal from '../../../components/GenericModal'
import DistributionPlanDetails from '../DistributionPlanDetails'
import { CreateAndEditDistributionPlan } from '../../DistributionSeriesAndPlan/DistributionPlan/CreateAndEditDistributionPlan'
import { SurveyProjectButtons } from './styles'
import TourOutlinedIcon from '@mui/icons-material/TourOutlined'
import CustomTooltip from '../../../components/CustomTooltip'
import SandboxBadge from '../../../components/SandboxBadge'
import { shade, yellow } from '../../../colors'
import { red } from '@mui/material/colors'
import { useParams } from 'react-router-dom'
import { getStatusLoading, getCourseLoading, getSlotSections } from '../../../store/slices/distributionPlanSlice/selectors'
import { useQueryClient } from '@tanstack/react-query'
import { useUserRole } from '../../../hooks/useUserRole'
import SandboxModeModal from '../../DistributionSeriesAndPlan/SandboxModeModal'
import { type DropdownItemConfig } from '../../../components/Dropdown'
import { Actions } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import ROUTES from '../../../consts/paths'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import SlotEmailSettingsFeature from '../../SlotEmailSettingsFeature'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SurveyProjectReopenModal from './SurveyProjectReopenModal'
import Dropdown from '../../../components/Dropdown'
import ReportPermissionsFeature from '../../ReportPermissionsFeature'
import { ConfirmPublishModal } from './ConfirmPublishModal'
import { useSlotTaskContext } from '../../../contexts/SlotTaskContext'
import { ConfirmGoLiveModal } from './ConfirmGoLiveModal'
import { ConfirmCloseProjectModal } from './ConfirmCloseProjectModal'
import { type ISection, type ISectionRow } from '../../../models/TermsSection'
import SlotNodeWarningBanner from './SlotNodeWarningBanner'
import { handleSlotStatusUpdateResponse } from './slotStatusUpdateHandler'

interface CourseEvaluationHeaderProps {
  slotData: any | undefined
  // breadcrumb: CustomBreadcrumbT[]
  setGuidedTourModal: (value: boolean) => void
  emailEdit?: boolean
  emailSettingsButtonRef?: React.RefObject<HTMLDivElement>
}

export const chipColors: Record<string, string> = {
  Draft: 'default',
  Published: 'primary',
  Live: 'success'
}

const CourseEvaluationHeader: React.FC<CourseEvaluationHeaderProps> = ({ slotData, setGuidedTourModal, emailEdit, emailSettingsButtonRef }) => {
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showConfirmPublishModal, setShowConfirmPublishModal] = useState(false)
  const { openBlockedModal, setTask, checkIfBlocked } = useSlotTaskContext()
  const [showConfirmGoLiveModal, setShowConfirmGoLiveModal] = useState(false)
  const [showConfirmCloseProjectModal, setShowConfirmCloseProjectModal] = useState(false)
  const [showReportPermissions, setShowReportPermissions] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)

  const slot = slotData?.data
  const permissions = slotData?.permissions
  const slotSections = getSlotSections()

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const params = new URLSearchParams(window.location.search)
  const isNavigatePresent = params.has('navigate')
  const statusLoading = getStatusLoading()
  const courseLoading = getCourseLoading()
  const isButtonLoading = statusLoading || courseLoading
  const status = slot?.slotNodeDetails?.slotStatus ?? 'default'
  const nodeWarnings = slot?.slotNodeDetails?.warnings ?? []
  const isPublishButtonShow = status === SlotStatus.Draft
  const isPublishedButtonsShow = status === SlotStatus.Published
  const isLiveButtonShow = status === SlotStatus.Live

  const eligibleSections = useMemo<ISection[]>(() => (slotSections?.data ?? [])
    .map((row: ISectionRow) => row.data)
    .filter((s: ISection) =>
      s.slotStatus === SlotStatus.Published &&
      new Date(s.startTimestamp).getTime() === new Date(slot?.slotStartTimestamp).getTime()
    ), [slotSections?.data, slot?.slotStartTimestamp])
  const chipVariant = status === SlotStatus.Draft ? 'outlined' : 'filled'
  const queryClient = useQueryClient()

  const breadcrumbPrefixList: CustomBreadcrumbT[] = [{
    title: PAGE_TITLES.DISTRIBUTION_SERIES,
    path: ROUTES.DISTRIBUTION_SERIES
  }, { title: slot?.title ?? '', path: ROUTES.COURSE_AND_ENROLLMENT }]

  const { id } = useParams()
  const isProjectStartDateInPast = slot && new Date(slot.slotStartTimestamp) <= new Date()

  const chipColor: any = chipColors[status]
  const [openModal, setOpenModal] = useState<boolean>(false)
  const showHeaderReopenAction = status === SlotStatus.Closed && permissions?.includes(Actions.EDIT)
  const projectId = params.get('projectId') ?? ''
  const canEditReportPermissions = Boolean(permissions?.includes(Actions.EDIT))

  // CR-341: sandbox is system-level only, and gated on `hasGlobalRole` (any GLOBAL role held)
  // rather than `isGlobalRole` (the active one) — someone who holds Account Owner but has
  // switched to Owner must keep sight of their own sandbox project.
  const { hasGlobalRole } = useUserRole()
  const [showSandboxMode, setShowSandboxMode] = useState<boolean>(false)

  // The chip's value. This page reads the slot, and `isSandbox` is a series field, so it is read
  // from the series — the same query `SandboxModeModal` makes when opened, so the two share one
  // cache entry rather than costing a request each. Skipped entirely when the URL carries no
  // `projectId`, which is also when the modal could not be opened.
  // Read from the slot this page already has. Sandbox moved from the series to the term
  // (backend `80a0e6006`), which also removed the read that used to sit here — with it went the
  // window where the answer was still pending and `Go Live` was offered on a sandboxed project
  // (found by review on #1940), and the dependence on `projectId` being in the URL.
  const isSandbox = slot?.isSandbox === true

  // Header overflow (⋮) menu: Reopen only when closed.
  const headerMenuItems: DropdownItemConfig[] = [
    {
      // Ungated since the full-cycle spec (approved 2026-09-21): a sandboxed term collects real
      // responses and produces a real report, so there is something for these permissions to
      // govern. The reason it was disabled — that the term never opens — no longer holds.
      text: 'Edit Report Permissions',
      action: () => { setShowReportPermissions(true) }
    },
    {
      // On/Off chip plus a modal, the shape Sync Course Sections established: the row says where
      // the setting stands, and the change is confirmed inside rather than on the click.
      text: 'Sandbox Mode',
      action: () => { setShowSandboxMode(true) },
      chip: { label: isSandbox ? 'On' : 'Off', color: isSandbox ? 'warning' : 'default' },
      // Hidden outright rather than disabled, on two counts. The role: an Academic Unit Owner who
      // could switch this on would lock themselves out of the term, and only a global-role user
      // could undo it. And the status: sandbox can only be changed before a term opens — the
      // backend answers 422 once any of *its own* sections is Live or Closed — so on a Live or
      // Closed one the row would offer something that can never be done.
      visible: hasGlobalRole && (status === SlotStatus.Draft || status === SlotStatus.Published)
    },
    { text: 'Reopen Survey', action: () => { setShowReopenModal(true) }, visible: showHeaderReopenAction }
  ]

  const invalidateSlot = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['slotDataById', id] })
  }

  // Reads URL at call time so async-task callbacks pick up any view changes that happened
  // while the task was running (e.g., Close Project flipping ?view from live_upcoming → closed).
  const refreshSlotData = (): void => {
    invalidateSlot()
    void dispatch(getSlotSectionApi({ urlId: id, params: new URLSearchParams(window.location.search) }))
  }

  const handleUpdateSlotStatus = async (): Promise<void> => {
    const updateStatus = status === SlotStatus.Draft ? SlotStatus.Published : SlotStatus.Draft
    const successMessage = updateStatus === SlotStatus.Published
      ? 'Survey project published successfully.'
      : 'Survey project reverted to draft.'
    const response = await dispatch(updateSlotStatusApi({ urlId: slot?.slotId, data: { status: updateStatus, apply_to_sections: true } }))
    handleSlotStatusUpdateResponse(response, successMessage, { setTask, refreshSlotData, invalidateSlot, openBlockedModal })
  }

  const handleGoLive = async (applyToSections: boolean): Promise<void> => {
    const response = await dispatch(updateSlotStatusApi({ urlId: slot?.slotId, data: { status: SlotStatus.Live, apply_to_sections: applyToSections } }))
    handleSlotStatusUpdateResponse(response, 'Survey project is now live.', { setTask, refreshSlotData, invalidateSlot, openBlockedModal })
  }

  const handleCloseProject = async (): Promise<void> => {
    const response = await dispatch(updateSlotStatusApi({ urlId: slot?.slotId, data: { status: SlotStatus.Closed, apply_to_sections: true } }))
    handleSlotStatusUpdateResponse(response, 'Survey project has been closed.', { setTask, refreshSlotData, invalidateSlot, openBlockedModal })
  }

  const handleCloseModal = (): void => {
    setOpenModal(false)
  }

  const handleEditButtonClick = (): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) setOpenModal(true) })
  }

  const handleSaved = (): void => {
    void dispatch(getSlotSectionApi({ urlId: id, params }))
  }

  const getPublishButtonTooltip = (): string => {
    if (!permissions?.includes(Actions.EDIT)) {
      return 'You do not have permission to perform this action'
    }
    if (slotSections?.data?.length === 0 && status === SlotStatus.Draft) {
      return 'Must have at least one course section survey to publish'
    }
    if (isProjectStartDateInPast && status === SlotStatus.Draft) {
      return 'Survey Project Start Date must be in the future to move to Published state.'
    }
    return ''
  }

  // • Disable Publish when user lacks EDIT permission, there are no sections, or (in Draft) the project start date is already in the past
  const publishButtonDisabled = (!permissions?.includes(Actions.EDIT) || (slotSections?.data?.length === 0) || (isProjectStartDateInPast && status === SlotStatus.Draft)) && status === SlotStatus.Draft

  const publishButton = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <CustomTooltip title={getPublishButtonTooltip()} placement='top-start'>
        <GenericButton
          label='Publish'
          buttonType={ButtonType.Primary}
          loading={isButtonLoading}
          onClick={() => { void checkIfBlocked().then((blocked) => { if (!blocked) setShowConfirmPublishModal(true) }) }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
          disabled={publishButtonDisabled}
        />
      </CustomTooltip>
      <Tooltip
        title={'Publish makes the survey visible to instructors and students. It requires at least one course section and a future start date'}
        placement="top"
      >
        <HelpOutlineIcon color="action" fontSize="small" sx={{ marginLeft: 0.5 }} />
      </Tooltip>
    </Box>
  )

  const revertToDraftButton = (
    <CustomTooltip
      title={!permissions?.includes(Actions.EDIT) ? 'You do not have permission to perform this action' : ''}
      placement='top-start'
    >
      <GenericButton
        label='Revert to Draft'
        buttonType={ButtonType.Secondary}
        loading={isButtonLoading}
        onClick={() => { void checkIfBlocked().then((blocked) => { if (!blocked) void handleUpdateSlotStatus() }) }}
        style={{ flexShrink: 0 }}
        disabled={!permissions?.includes(Actions.EDIT)}
      />
    </CustomTooltip>
  )

  const goLiveButton = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <CustomTooltip
        title={!permissions?.includes(Actions.EDIT) ? 'You do not have permission to perform this action' : ''}
        placement='top-start'
      >
        <GenericButton
          label='Go Live'
          buttonType={ButtonType.Primary}
          loading={isButtonLoading}
          onClick={() => { void checkIfBlocked().then((blocked) => { if (!blocked) setShowConfirmGoLiveModal(true) }) }}
          style={{ flexShrink: 0 }}
          disabled={!permissions?.includes(Actions.EDIT)}
        />
      </CustomTooltip>
      <Tooltip
        title='Going Live will distribute surveys to eligible students. Sections that have not yet started will remain in their current state unless you choose to apply the change to all sections.'
        placement='top'
      >
        <HelpOutlineIcon color='action' fontSize='small' sx={{ marginLeft: 0.5 }} />
      </Tooltip>
    </Box>
  )

  const closeProjectButton = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <CustomTooltip
        title={!permissions?.includes(Actions.EDIT) ? 'You do not have permission to perform this action' : ''}
        placement='top-start'
      >
        <GenericButton
          label='Close Project'
          buttonType={ButtonType.Destructive}
          loading={isButtonLoading}
          onClick={() => { void checkIfBlocked().then((blocked) => { if (!blocked) setShowConfirmCloseProjectModal(true) }) }}
          style={{ flexShrink: 0 }}
          disabled={!permissions?.includes(Actions.EDIT)}
        />
      </CustomTooltip>
      <Tooltip
        title='Closing this project is permanent. Live sections will be closed and reports generated. Published sections that never went live will revert to Draft.'
        placement='top'
      >
        <HelpOutlineIcon color='action' fontSize='small' sx={{ marginLeft: 0.5 }} />
      </Tooltip>
    </Box>
  )
  const isEditActionAllowed = slot?.actions?.includes(Actions.EDIT) || slot?.actions?.includes(Actions.CREATE)
  const isShowEditButton = permissions?.includes(Actions.EDIT)

  const isSlotAnonymous = slot?.isSlotAnonymous ?? false
  return (
    <React.Fragment>
      <PageHeader
        title={slot?.title ?? ''}
        showBackButton
        breadcrumb={breadcrumbPrefixList}
        controls={
          !isNavigatePresent &&
          <SurveyProjectButtons>
            <CustomTooltip title='Start Guided Tour' style={{ cursor: 'pointer' }} >
              <TourOutlinedIcon onClick={() => { setGuidedTourModal(true) }} />
            </CustomTooltip>
            {isShowEditButton && (
              <GenericButton buttonType={ButtonType.Secondary} icon={<CreateOutlinedIcon />} onClick={handleEditButtonClick} />
            )}
            {emailEdit &&
              <Box ref={emailSettingsButtonRef}>
                <GenericButton
                  key="confirmButton"

                  disabled={isNavigatePresent && !isEditActionAllowed}
                  buttonType={ButtonType.Secondary}
                  label='Email Settings'
                  startIcon={<MailOutlinedIcon />}
                  onClick={() => { setShowEmailModal(true) }} />
              </Box>
            }
            {isPublishButtonShow && publishButton}
            {isPublishedButtonsShow && revertToDraftButton}
            {/* Offered on a sandboxed term like any other. The lifecycle freeze this used to
                guard against was withdrawn by the full-cycle spec (approved 2026-09-21): a
                rehearsal now opens, emails, closes and reports, and running it is the whole
                point. What makes it a rehearsal is its audience, not its inability to start. */}
            {isPublishedButtonsShow && goLiveButton}
            {isLiveButtonShow && closeProjectButton}

            <Dropdown
              dropdownType='action'
              menuItems={headerMenuItems}
              extraButtonProps={{ style: { p: 0 }, buttonType: ButtonType.Ghost }}
            />
          </SurveyProjectButtons>

        }
        statusChip={
          <React.Fragment>
            <Chip size="small" label={status} variant={chipVariant} color={chipColor} />
            {/* Back beside the status chip, 2026-09-23, after being removed on 2026-09-20 as a
                duplicate of the banner below. It is not one: the banner is the explanation and
                scrolls away with the page, while this is the marker that sits with the other
                status chips and is the thing a reader scans for. The badge now carries the
                explanation in a tooltip, so the two are a label and its detail rather than the
                same sentence twice. */}
            <SandboxBadge isSandbox={isSandbox} />
            {isSlotAnonymous && (
              <Chip
                label="Unverified Submissions"
                variant="filled"
                size="small"
                sx={{
                  borderRadius: '6px',
                  border: `1px solid ${yellow[100]}`,
                  fontWeight: 500,
                  fontSize: '12px',
                  padding: '0px 8px',
                  color: red[800],
                  backgroundColor: shade[1000]
                }}
              />
            )}
          </React.Fragment>
        }
      />
      <SlotNodeWarningBanner
        warnings={nodeWarnings}
        forceError={status === SlotStatus.Closed}
      />
      {slot && <DistributionPlanDetails slot={slot} />}
      <GenericModal
        isOpen={openModal}
        maxWidth="sm"
        title={`Edit ${slot?.term?.termName} Survey Project`}
        onClose={() => { setOpenModal(false) }}
        action={false}
      >
        {/* `onTaskComplete` uses `refreshSlotData`, which reads the view from the live URL — a
            release-date clamp finishes minutes after the save, by which time `handleSaved`'s
            captured params may point at a tab the admin has left. */}
        <CreateAndEditDistributionPlan
          selectedRow={slot}
          handleCloseModal={handleCloseModal}
          onSaved={handleSaved}
          onTaskComplete={refreshSlotData}
        />
      </GenericModal>
      {/* Confirm Publish Modal */}
      <GenericModal
        isOpen={showConfirmPublishModal}
        maxWidth="sm"
        title="Publish Survey Project"
        onClose={() => { setShowConfirmPublishModal(false) }}
        action={false}
      >
        <ConfirmPublishModal
          handleCloseModal={() => { setShowConfirmPublishModal(false) }}
          onConfirm={() => {
            void handleUpdateSlotStatus()
            setShowConfirmPublishModal(false)
          }}
          slot={slot}
        />
      </GenericModal>
      {/* Go Live Modal. No sandbox check on either this or the button that opens it — a sandboxed
          term goes Live on its own schedule now, and the server no longer refuses it. */}
      <GenericModal
        isOpen={showConfirmGoLiveModal}
        maxWidth='sm'
        title='Go Live'
        onClose={() => { setShowConfirmGoLiveModal(false) }}
        action={false}
      >
        <ConfirmGoLiveModal
          handleCloseModal={() => { setShowConfirmGoLiveModal(false) }}
          onConfirm={(applyToSections) => {
            void handleGoLive(applyToSections)
            setShowConfirmGoLiveModal(false)
          }}
          eligibleSections={eligibleSections}
        />
      </GenericModal>
      {/* Close Project Modal */}
      <GenericModal
        isOpen={showConfirmCloseProjectModal}
        maxWidth='sm'
        title='Close Survey Project'
        onClose={() => { setShowConfirmCloseProjectModal(false) }}
        action={false}
      >
        <ConfirmCloseProjectModal
          handleCloseModal={() => { setShowConfirmCloseProjectModal(false) }}
          onConfirm={() => {
            void handleCloseProject()
            setShowConfirmCloseProjectModal(false)
          }}
        />
      </GenericModal>
      {
        showEmailModal && <SlotEmailSettingsFeature slot={slot} handleCloseModal={() => { setShowEmailModal(false) }} isOpen={showEmailModal} />
      }

      {showSandboxMode && (
        <SandboxModeModal
          slotId={slot?.slotId ?? id ?? ''}
          slotTitle={slot?.title}
          canEdit={hasGlobalRole}
          isOpen={showSandboxMode}
          handleCloseModal={() => { setShowSandboxMode(false) }}
        />
      )}

      {showReportPermissions && slot && (
        <ReportPermissionsFeature
          scope="slot"
          scopeId={slot.slotId}
          seriesId={projectId}
          scopeLabel={slot.term?.termName}
          canEdit={canEditReportPermissions}
          isOpen={showReportPermissions}
          handleCloseModal={() => { setShowReportPermissions(false) }}
        />
      )}

      {slot && (
        <SurveyProjectReopenModal
          slot={slot}
          isOpen={showReopenModal}
          onClose={() => { setShowReopenModal(false) }}
        />
      )}
    </React.Fragment>
  )
}

export default CourseEvaluationHeader
